import { useCallback, useEffect, useState } from "react"
import { Navigate, useLocation } from "react-router-dom"
import { AppShell } from "components/dashboard/app-shell"
import { AgendaContent } from "components/agenda/AgendaContent"
import { useUser } from "contexts/UserContext"
import { useResourceSubscription } from "contexts/NotificationsContext"
import { getAppointments, updateAppointment, deleteAppointment } from "services/appointments"
import { getTasks } from "services/tasks"
import type { CalendarEvent } from "../types/agenda"
import { appointmentToCalendarEvent, calendarEventToAppointment } from "../utils/appointmentAdapter"

export default function AgendaPageNew() {
  // Usa il UserContext per i dati utente condivisi
  const { user, loading } = useUser()
  const location = useLocation()
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [eventsLoading, setEventsLoading] = useState(true)
  const [highlightedEventId, setHighlightedEventId] = useState<number | null>(null)
  const [openDetailId, setOpenDetailId] = useState<number | null>(null)

  // Leggi l'eventuale highlightEventId o openDetailId passato dalla navigazione
  useEffect(() => {
    const state = location.state as { highlightEventId?: number; openDetailId?: number } | null
    if (state?.highlightEventId) {
      setHighlightedEventId(state.highlightEventId)
      window.history.replaceState({}, "")
    }
    if (state?.openDetailId) {
      setOpenDetailId(state.openDetailId)
      window.history.replaceState({}, "")
    }
  }, [location.state])

  // Rimuovi l'highlight al primo click successivo dell'utente
  useEffect(() => {
    if (highlightedEventId === null) return
    const clearHighlight = () => setHighlightedEventId(null)
    const timer = setTimeout(() => {
      document.addEventListener("click", clearHighlight, { once: true })
    }, 500)
    return () => {
      clearTimeout(timer)
      document.removeEventListener("click", clearHighlight)
    }
  }, [highlightedEventId])

  const fetchEvents = useCallback(async () => {
    try {
      setEventsLoading(true)

      // Fetch appointments e task in parallelo
      const [appointmentsRes, tasksRes] = await Promise.all([
        getAppointments(),
        getTasks().catch(() => ({ data: [] })),
      ])

      // Mappa appointments backend → frontend
      const appointmentsData = Array.isArray(appointmentsRes.data) ? appointmentsRes.data : appointmentsRes.data?.results || []
      const mappedAppointments = appointmentsData.map(appointmentToCalendarEvent)

      // Mappa task con due_date → CalendarEvent
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const tasksData = Array.isArray(tasksRes.data) ? tasksRes.data : tasksRes.data?.results || []
      const mappedTasks: CalendarEvent[] = tasksData
        .filter((task: any) => task.due_date)
        .map((task: any) => {
          const dueDate = new Date(task.due_date)
          dueDate.setHours(0, 0, 0, 0)
          const isOverdue = !task.is_completed && dueDate < today
          return {
            id: `task-${task.id}`,
            title: task.title,
            start_time: '',
            start_date: task.due_date,
            type: 'task' as const,
            description: task.description || '',
            source: 'task' as const,
            due_date: task.due_date,
            is_overdue: isOverdue,
            is_completed: task.is_completed || false,
            priority: task.priority,
          }
        })

      setEvents([...mappedAppointments, ...mappedTasks])
    } catch (err) {
      console.error("Failed to fetch events:", err)
      setEvents([])
    } finally {
      setEventsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchEvents()
  }, [user, fetchEvents])

  // Realtime: l'agenda mostra appointment + task con due_date → ascolta entrambi
  useResourceSubscription('appointment', () => fetchEvents())
  useResourceSubscription('task', () => fetchEvents())

  const handleCreateEvent = (savedEvent: CalendarEvent) => {
    // L'evento è già stato creato da NewEventModal, aggiorna solo lo state locale
    setEvents([...events, savedEvent])
  }

  const handleUpdateEvent = async (id: number, eventData: Partial<CalendarEvent>) => {
    try {
      // Mappa i campi frontend → backend
      const appointmentData = calendarEventToAppointment(eventData)
      const response = await updateAppointment(id, appointmentData)
      // Mappa la risposta backend → frontend
      const mappedEvent = appointmentToCalendarEvent(response.data)
      setEvents(events.map(e => e.id === id ? mappedEvent : e))
    } catch (err) {
      console.error("Failed to update event:", err)
      alert("Errore durante l'aggiornamento dell'evento")
    }
  }

  const handleDeleteEvent = async (id: number) => {
    try {
      await deleteAppointment(id)
      setEvents(events.filter(e => e.id !== id))
    } catch (err) {
      console.error("Failed to delete event:", err)
      alert("Errore durante l'eliminazione dell'evento")
    }
  }

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          background: `linear-gradient(to bottom right, var(--hexa-bg-gradient-start), var(--hexa-bg-gradient-mid), var(--hexa-bg-gradient-end))`
        }}
      >
        <div className="text-[var(--hexa-text-primary)] text-lg">Caricamento...</div>
      </div>
    )
  }

  if (!user && !localStorage.getItem('accessToken')) {
    return <Navigate to="/login" replace />
  }

  return (
    <AppShell currentUser={user}>
      <AgendaContent
        events={events}
        onCreateEvent={handleCreateEvent}
        onUpdateEvent={handleUpdateEvent}
        onDeleteEvent={handleDeleteEvent}
        loading={eventsLoading}
        workingHours={user?.working_hours}
        highlightedEventId={highlightedEventId}
        openDetailId={openDetailId}
      />
    </AppShell>
  )
}
